import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { receiveCustomMetrics, trackCustomMetric, getSearchPageData } from '../metrics/track-params';
import { pollPageView, setPagePropertiesData } from '../metrics/metrics-helpers';
import { getSlugInfo } from '.';

import useHawksearchComponent from './useHawksearchComponent';

/**
 * This hook is responsible for analytics tracking and redirection based on the HawkSearch data.
 */
export default (tabType, supportPages, searchKey) => {
    const hawksearchResults = useHawksearchComponent(); // TODO: When converting to SSR, must recreate this useHawkSearch hook.
    const { vanityUrl, analyticsData } = getSlugInfo() || {};
    const { simple_title } = analyticsData || {};
    const pageViewUrl = useSelector(
        state => state && state.metrics && state.metrics.pageView && state.metrics.pageView.vanityUrl,
    );
    const searchResultsCustomMetrics = useSelector(
        state => state && state.metrics && state.metrics.customMetrics && state.metrics.customMetrics.searchResults,
    );
    const dispatch = useDispatch();

    const { type = 'products' } = hawksearchResults || {};
    const searchResults = hawksearchResults && hawksearchResults[type];
    const { Redirect, Pagination, Keyword, TrackingId } = searchResults || {};
    const { NofResults } = Pagination || {};
    const redirectLocation = Redirect && Redirect.Location;
    const pageViewTriggered = pageViewUrl && pageViewUrl === vanityUrl;
    const { searchTerm } = searchResultsCustomMetrics || {};
    const trackSearchResults = useCallback(
        searchResultsParameters => {
            dispatch(trackCustomMetric('searchResults', searchResultsParameters));
        },
        [dispatch, trackCustomMetric],
    );

    const isSupport = tabType === 'support';
    const isContent = type === 'content';
    const searchKeyword = isSupport ? searchKey : Keyword;

    const setSearchResultsMetrics = useCallback(searchResultsParameters => {
        try {
            const searchData = getSearchPageData(searchResultsParameters);
            setPagePropertiesData(searchData);

            dispatch(
                receiveCustomMetrics({
                    customMetrics: {
                        searchResults: searchResultsParameters,
                    },
                }),
            );
        } catch (e) {}
    }, []);

    useEffect(() => {
        return () => {
            setSearchResultsMetrics({});
        };
    }, []);

    useEffect(() => {
        let noActions =
            (!searchResults && !isSupport) ||
            (!supportPages && isSupport) ||
            typeof searchKeyword !== 'string' ||
            redirectLocation;
        let currentSearch;
        try {
            currentSearch = window.pagePropertiesData.search.searchString;
        } catch (e) {}
        // subsequent searches on the search page will trigger searchResults event but not the page view event.
        if (
            !noActions &&
            (typeof searchTerm !== 'string' || searchTerm !== searchKeyword) &&
            searchKeyword !== currentSearch
        ) {
            setSearchResultsMetrics({
                numSearchResults: NofResults,
                searchTerm: Keyword,
                hsResID: TrackingId,
            });
        } else {
            setSearchResultsMetrics({});
        }
    }, [searchKeyword]);

    // for redirecting
    useEffect(() => {
        let validRedirectlocation = typeof redirectLocation === 'string' && tabType && !isSupport;

        if (validRedirectlocation) {
            try {
                let isRedirecting;
                trackSearchResults({
                    numSearchResults: NofResults,
                    searchTerm: Keyword,
                    eventCallback: () => {
                        // This callback executes for every GTM script we have.
                        if (!isRedirecting) {
                            isRedirecting = true;
                            window.location.replace(redirectLocation);
                        }
                    },
                });
            } catch (e) {}
        }
    }, [redirectLocation, isSupport]);

    return null;
};
